import React, {useContext, useState, useEffect, useRef, useMemo, useCallback} from 'react';
import {useBreadcrumbComponent} from "@common/useBreadcrumbComponent";
import axios from "@common/axios";
import TableAntD from "@common/TableAntD";
import {UserContext} from "@context/UserContext";
import dayjs from 'dayjs';
import Button from "@common/Button";
import {
    CheckCircleRounded,
    ContentCopyRounded,
    DataObjectRounded,
    GradingRounded,
    IosShare,
    SaveRounded, ScheduleSend
} from "@mui/icons-material";
import {BrandContext} from "@context/BrandContext";
import {useLocation, useSearchParams} from "wouter";
import PageHeader from "@common/PageHeader";
import Tooltip from "@common/Tooltip";
import GenerateActivityReportSummaryDialog from "./GenerateActivityReportSummaryDialog";
import SaveSummaryDialog from "./SaveSummaryDialog";
import ScheduleReportDialog from "./ScheduleReportDialog";
import ExportToCsvDropdownButton from '@common/ExportToCsvDropdownButton';
import {Dropdown, Tag, Popover} from 'antd';
import CircularLoader from "@common/CircularLoader";
import InputAntdInline from "@common/InputAntdInline";
import useSavedReport from "./useSavedReport";

const ActivityReportSummary = ({ classIdParam }) => {
    const { user } = useContext(UserContext);
    const brandConfig = useContext(BrandContext);
    const [location, navigate] = useLocation();
    const [searchParams] = useSearchParams();
    const savedReportId = searchParams.get('savedReportId'); // Check if viewing a saved report
    const BreadcrumbBar = useBreadcrumbComponent({ dropLast: true });
    const [refetchTrigger, setRefetchTrigger] = useState(0);
    const [copiedStudentId, setCopiedStudentId] = useState(null);
    const copyTimeoutRef = useRef(null);
    const [isAIReportGeneratorDialogVisible, setAIReportGeneratorDialogVisible] = useState(false);
    const [isSaveReportDialogVisible, setSaveReportDialogVisible] = useState(false);
    const [isScheduleReportDialogVisible, setScheduleReportDialogVisible] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [className, setClassName] = useState(null);
    const [classAverages, setClassAverages] = useState(null);

    const {
        reportName, reportDescription,
        savedStartDate, savedEndDate, savedMetrics,
        savedDateRangeType, savedAdaptiveRangeValue, savedAdaptiveRangeUnit,
        loading, savingName, savingDescription,
        handleUpdateReportName, handleUpdateDescription
    } = useSavedReport({ savedReportId, setTableData, setClassName, setClassAverages });

    // Extract parameters from URL
    const metricsParam = searchParams.get('metrics');
    const metrics = useMemo(() =>
        metricsParam ? metricsParam.split(',') : null,
        [metricsParam]
    );
    const startDateParam = searchParams.get('startDate');
    const endDateParam = searchParams.get('endDate');
    const excludeInactive = searchParams.get('excludeInactive') === 'true';
    const includeBelowAverage = searchParams.get('includeBelowAverage') !== 'false'; // Default to true

    const metricLabels = {
        sessionsStarted: 'Sessions Started',
        lastLogin: 'Last Login',
        exercisesComplete: 'Exercises Complete',
        walkthroughsComplete: 'Walkthroughs Complete',
        quizzesComplete: 'Section Quizzes Complete',
        quizQuestions: 'Questions Complete',
        testsComplete: `${brandConfig?.testsName || 'Tests'} Complete`,
        scorchersComplete: 'Scorchers Complete'
    };

    const getDisplayMetrics = () => {
        const metricsToUse = savedReportId ? savedMetrics : metrics;
        if (!metricsToUse || metricsToUse.length === 0) {
            return { isAll: true, labels: [] };
        }
        return { isAll: false, labels: metricsToUse.map(m => metricLabels[m] || m) };
    };

    const getDisplayDateRange = () => {
        if (savedReportId) {
            // For saved reports, check if it's adaptive or fixed
            if (savedDateRangeType === 'adaptive' && savedAdaptiveRangeValue && savedAdaptiveRangeUnit) {
                const unitLabel = savedAdaptiveRangeUnit === 'days' ? 'day' :
                                  savedAdaptiveRangeUnit === 'weeks' ? 'week' : 'month';
                const plural = savedAdaptiveRangeValue > 1 ? 's' : '';
                return `Previous ${savedAdaptiveRangeValue} ${unitLabel}${plural}`;
            } else if (savedStartDate && savedEndDate) {
                const format = user?.getDateFormatString?.() || 'DD/MM/YYYY';
                return `${dayjs(savedStartDate).format(format)} - ${dayjs(savedEndDate).format(format)}`;
            }
        } else {
            // For generated reports, use URL params
            if (startDateParam && endDateParam) {
                const format = user?.getDateFormatString?.() || 'DD/MM/YYYY';
                return `${dayjs(startDateParam).format(format)} - ${dayjs(endDateParam).format(format)}`;
            }
            // No date params means default last 7 days
            return 'Previous 7 days';
        }
        return null;
    };

    const renderSubtitle = () => {
        const dateRange = getDisplayDateRange();
        const { isAll, labels } = getDisplayMetrics();

        const metricsContent = (
            <ul style={{ margin: 0, paddingLeft: '16px' }}>
                {labels.map((label, idx) => (
                    <li key={idx}>{label}</li>
                ))}
            </ul>
        );

        return (
            <span>
                {savedReportId && (
                    <span style={{ display: 'block', marginBottom: '8px' }}>
                        <InputAntdInline
                            value={reportDescription || ''}
                            textType="span"
                            onSave={handleUpdateDescription}
                            isLoading={savingDescription}
                            placeholder="description"
                            inputPlaceholder="Enter a description"
                            fieldLabel="report description"
                            minWidth={200}
                            style={{
                                fontStyle: reportDescription ? 'normal' : 'italic',
                                color: reportDescription ? 'inherit' : '#666'
                            }}
                        />
                    </span>
                )}
                <span style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                    {dateRange && <Tag>{dateRange}</Tag>}
                    {isAll ? (
                        <Tag color="blue">All Metrics</Tag>
                    ) : (
                        <Popover content={metricsContent} title="Selected Metrics" placement="bottom">
                            <Tag
                                color="blue"
                                style={{ cursor: 'pointer' }}
                                aria-label={`${labels.length} metrics selected: ${labels.join(', ')}`}
                            >
                                {labels.length} Metrics
                            </Tag>
                        </Popover>
                    )}
                </span>
            </span>
        );
    };

    useEffect(() => {
        if (!brandConfig.useAIReportGenerator) {
            navigate(`/web/ui/view/reports/activityReport/${classIdParam}`);
        }
    }, [brandConfig.useAIReportGenerator, classIdParam, navigate]);

    const fetchData = useCallback((pagingParams, handleTableData, handlePagingData, abortController) => {
        const isUSLocale = user.locale === 'en_US';

        // Use URL params if available, otherwise default to last 7 days
        const start = startDateParam ? dayjs(startDateParam) : dayjs().subtract(6, 'day');
        const end = endDateParam ? dayjs(endDateParam) : dayjs();

        const formatDate = (date) => {
            const day = date.date().toString().padStart(2, '0');
            const month = (date.month() + 1).toString().padStart(2, '0');
            const year = date.year();
            return isUSLocale ? `${month}/${day}/${year}` : `${day}/${month}/${year}`;
        };

        // Fetch class statistics report
        axios.get('/web/ui/report/teacher/activity/classStatisticsReport', {
            params: {
                classId: classIdParam,
                startSearch: formatDate(start),
                endSearch: formatDate(end),
                searchPeriod: 'DATE_RANGE'
            },
            signal: abortController.signal
        })
            .then(response => {
                console.log('Class Statistics Report:', response.data);
            })
            .catch(error => {
                if (error.name !== 'CanceledError') {
                    console.error('Error fetching class statistics:', error);
                }
            });

        // Fetch class student comments
        axios.get('/web/ui/report/teacher/activity/classStudentComments', {
            params: {
                classId: classIdParam,
                startSearch: formatDate(start),
                endSearch: formatDate(end),
                searchPeriod: 'DATE_RANGE',
                ...(metrics && { metrics: metrics.join(',') }),
                ...(excludeInactive && { excludeInactive: true }),
                ...(!includeBelowAverage && { includeBelowAverage: false })
            },
            signal: abortController.signal
        })
            .then(response => {
                console.log('Class Student Comments:', response.data);
                const students = response.data?.students || [];
                const classNameFromResponse = response.data?.className;
                const classAveragesFromResponse = response.data?.classAverages;

                if (classNameFromResponse) {
                    setClassName(classNameFromResponse);
                }

                if (classAveragesFromResponse) {
                    setClassAverages(classAveragesFromResponse);
                }

                const data = students.map((student, index) => ({
                    key: student.studentId || index,
                    studentName: student.name,
                    studentId: student.studentId,
                    email: student.email,
                    comment: student.comment
                }));

                setTableData(data);
                handleTableData(data);
                handlePagingData({
                    pageSize: pagingParams.pageSize,
                    totalResults: students.length,
                });
            })
            .catch(error => {
                if (error.name !== 'CanceledError') {
                    console.error('Error fetching student comments:', error);
                    setTableData([]);
                    handleTableData([]);
                    handlePagingData({
                        pageSize: pagingParams.pageSize,
                        totalResults: 0
                    });
                }
            });
    }, [user.locale, startDateParam, endDateParam, classIdParam, metrics, excludeInactive, includeBelowAverage]);

    const pagingDeps = useMemo(() =>
        [refetchTrigger, metrics, startDateParam, endDateParam, excludeInactive, includeBelowAverage],
        [refetchTrigger, metrics, startDateParam, endDateParam, excludeInactive, includeBelowAverage]
    );

    const handleCopyComment = (comment, studentId) => {
        navigator.clipboard.writeText(comment).then(() => {
            // displayToastMessage('Comment copied to clipboard', null, 'success', null, 2000);

            // Clear any existing timeout
            if (copyTimeoutRef.current) {
                clearTimeout(copyTimeoutRef.current);
            }

            setCopiedStudentId(studentId);

            // Set new timeout and store the ID
            copyTimeoutRef.current = setTimeout(() => {
                setCopiedStudentId(null);
                copyTimeoutRef.current = null;
            }, 2000);
        }).catch(err => {
            console.error('Failed to copy text:', err);
        });
    };

    const handleAIReportGeneratorClick = () => {
        setAIReportGeneratorDialogVisible(true);
    };

    const handleDialogClose = () => {
        setAIReportGeneratorDialogVisible(false);
    };

    const handleGenerateSuccess = () => {
        // Trigger refetch when generating report with same or different params
        setRefetchTrigger(prev => prev + 1);
        setAIReportGeneratorDialogVisible(false);
    };

    const handleSaveReportClick = () => {
        setSaveReportDialogVisible(true);
    };

    const handleSaveDialogClose = () => {
        setSaveReportDialogVisible(false);
    };

    const handleSaveSuccess = (savedReportId) => {
        // Navigate to the saved report view
        navigate(`/web/ui/view/reports/activityReport/${classIdParam}/activitySummary?savedReportId=${savedReportId}`);
    };

    const columns = useMemo(() => [
        {
            title: 'Student Name',
            dataIndex: 'studentName',
            width: '150px',
            key: 'studentName',
            render: (text) => <strong>{text}</strong>
        },
        {
            title: 'Comment',
            dataIndex: 'comment',
            key: 'comment',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Tooltip title={copiedStudentId === record.studentId ? 'Copied to clipboard' : 'Copy comment'}>
                    <Button
                        type={'text'}
                        aria-label={'Copy comment'}
                        onClick={() => handleCopyComment(record.comment, record.studentId)}
                    >
                        {copiedStudentId === record.studentId ? <CheckCircleRounded style={{color: 'var(--success-colour)'}} /> : <ContentCopyRounded />}
                    </Button>
                </Tooltip>
            )
        },
    ], [copiedStudentId]);

    const csvColumns = useMemo(() => [
        {
            title: 'Student Name',
            dataIndex: 'studentName',
            key: 'studentName',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Comment',
            dataIndex: 'comment',
            key: 'comment',
        },
    ], []);

    const getExportFileName = () => {
        if (savedReportId && reportName) {
            const sanitizedName = reportName.replace(/\s+/g, '-');
            const datePart = getDateRangeString();
            return datePart
                ? `${sanitizedName}_${datePart}.csv`
                : `${sanitizedName}.csv`;
        }
        if (!className) {
            return 'activity_summary_comments.csv';
        }
        const sanitizedClassName = className.replace(/\s+/g, '-');
        return `${sanitizedClassName}_activity_summary_comments.csv`;
    };

    const getDateRangeString = () => {
        const start = savedStartDate || startDateParam;
        const end = savedEndDate || endDateParam;
        if (start && end) {
            return `${dayjs(start).format('YYYY-MM-DD')}_to_${dayjs(end).format('YYYY-MM-DD')}`;
        }
        return null;
    };

    const handleExportJSON = () => {
        try {
            // Build export data from current state
            const exportData = {
                className: className,
                comments: tableData.map(student => ({
                    studentId: student.studentId,
                    name: student.studentName,
                    email: student.email,
                    comment: student.comment
                }))
            };

            // Create blob and download
            const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });

            // Generate timestamp
            const now = new Date();
            const timestamp =
                (now.getMonth() + 1).toString().padStart(2, '0') +
                now.getDate().toString().padStart(2, '0') +
                now.getHours().toString().padStart(2, '0') +
                now.getMinutes().toString().padStart(2, '0') +
                now.getSeconds().toString().padStart(2, '0');

            // Generate filename
            let fileName;
            if (savedReportId && reportName) {
                const sanitizedName = reportName.replace(/\s+/g, '-');
                const datePart = getDateRangeString();
                fileName = datePart
                    ? `${sanitizedName}_${datePart}.json`
                    : `${sanitizedName}.json`;
            } else {
                const baseName = className ? className.replace(/\s+/g, '-') : 'activity_summary';
                fileName = `${baseName}_comments_${timestamp}.json`;
            }

            // Download
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', fileName);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            displayToastMessage('JSON exported successfully', null, 'success');
        } catch (error) {
            console.error('Error exporting to JSON:', error);
            displayToastMessage('Failed to export JSON', null, 'error');
        }
    };

    const handleScheduledEmail = () => {
        setScheduleReportDialogVisible(true);
    };

    const exportMenuItems = useMemo(() => {
        const items = [
            ExportToCsvDropdownButton({
                data: tableData,
                columns: csvColumns,
                fileName: getExportFileName(),
                disabled: tableData.length === 0
            }),
            {
                key: 'json',
                label: 'Export to JSON',
                onClick: handleExportJSON,
                icon: <DataObjectRounded />
            }
        ];

        // Only show Scheduled Email option when not viewing a saved report
        if (!savedReportId) {
            items.push({
                key: 'scheduled-email',
                label: 'Scheduled Email',
                onClick: handleScheduledEmail,
                icon: <ScheduleSend />
            });
        }

        return items;
    }, [tableData, csvColumns, className, savedReportId, reportName, savedStartDate, savedEndDate, startDateParam, endDateParam]);

    if (loading) {
        return (
            <CircularLoader />
        );
    }

    return (
        <div>
            <BreadcrumbBar />
            <PageHeader
                title={savedReportId ? (
                    <InputAntdInline
                        value={reportName || 'Saved Activity Summary'}
                        textType="span"
                        onSave={handleUpdateReportName}
                        isLoading={savingName}
                        fieldLabel="report name"
                        minWidth={200}
                    />
                ) : className ? `Activity Summary for ${className}` : 'Activity Summary'}
                subtitle={renderSubtitle()}
                buttons={
                    <>
                        {!savedReportId && (
                            <Button
                                type={'outline'}
                                onClick={handleSaveReportClick}
                                icon={<SaveRounded />}
                            >
                                Save summary
                            </Button>
                        )}
                        <Button type={'outline'} onClick={handleAIReportGeneratorClick}>
                            <GradingRounded style={{ marginRight: '0.5rem' }} /> {savedReportId ? 'New Summary' : 'Regenerate summary'}
                        </Button>
                        {/*<ExportToCsvButton*/}
                        {/*    data={tableData}*/}
                        {/*    columns={csvColumns}*/}
                        {/*    fileName={getExportFileName()}*/}
                        {/*    disabled={tableData.length === 0}*/}
                        {/*/>*/}
                        <Dropdown
                            menu={{ items: exportMenuItems }}
                            trigger={['click']}
                        >
                            <Button
                                type={'outline'}
                                icon={<IosShare />}
                            >
                                Export
                            </Button>
                        </Dropdown>
                    </>
                }
            />
            <TableAntD
                key={savedReportId ? `saved-${savedReportId}` : 'generated'}
                dynamicWidth={true}
                columnData={columns}
                pagingDependencies={pagingDeps}
                {...(savedReportId
                    ? {
                        // When viewing saved report, use dataSource directly
                        dataSource: tableData
                    }
                    : {
                        // When generating new report, use paging with fetch function
                        pagingData: {
                            fetchFunction: fetchData,
                            params: { pageSize: 20 }
                        }
                    }
                )}
                rowKey="studentId"
                locale={{
                    emptyText: <span className="ch-warning-text">No students found</span>
                }}
            />
            <GenerateActivityReportSummaryDialog
                visible={isAIReportGeneratorDialogVisible}
                handleClose={handleDialogClose}
                onGenerateSuccess={handleGenerateSuccess}
                selectedClassId={classIdParam}
                initialStartDate={startDateParam ? dayjs(startDateParam) : dayjs().subtract(6, 'day')}
                initialEndDate={endDateParam ? dayjs(endDateParam) : dayjs()}
                initialMetrics={metrics}
                initialExcludeInactive={excludeInactive}
                initialIncludeBelowAverage={includeBelowAverage}
            />
            <ScheduleReportDialog
                visible={isScheduleReportDialogVisible}
                handleClose={() => setScheduleReportDialogVisible(false)}
                selectedClassId={classIdParam}
                initialDays={startDateParam && endDateParam
                    ? dayjs(endDateParam).diff(dayjs(startDateParam), 'day') + 1
                    : 7}
                initialMetrics={metrics}
            />
            <SaveSummaryDialog
                visible={isSaveReportDialogVisible}
                handleClose={handleSaveDialogClose}
                startDate={startDateParam ? dayjs(startDateParam) : dayjs().subtract(6, 'day')}
                endDate={endDateParam ? dayjs(endDateParam) : dayjs()}
                classId={classIdParam}
                studentComments={tableData}
                classAverages={classAverages}
                selectedMetrics={metrics}
                excludeInactiveStudents={excludeInactive}
                includeBelowAverage={includeBelowAverage}
                onSuccess={handleSaveSuccess}
            />
        </div>
    );
};

export default ActivityReportSummary;